// Why a crawl came back with nothing — and, more usefully, whether a human has
// to do something about it.
//
// The pipeline used to report every failed crawl as "blocked: WAF challenge",
// which meant four unrelated things at once: a real Sucuri interstitial, an
// expired certificate (Pacheco Ranch), a dead domain hijacked into a 30-deep
// redirect loop (Salamandre Wine Cellars), and a site that simply had no
// content. Stage 01 now names the cause, and the point of naming it is that the
// responses differ: most causes clear themselves on the next run, but a dead or
// hijacked domain will never come back on a timer and needs its website field
// corrected. Those two used to look identical in this UI.
//
// `error` arrives as "<cause>: <detail>", so the cause is the first token.

export const FAILURE_CAUSES = {
  waf_challenge: {
    label: 'Bot challenge',
    style: 'bg-amber-100 text-amber-800',
    needsAttention: false,
    hint: 'The site put a bot check in front of us. Retried automatically, and a browser render usually gets through.',
  },
  walled_midcrawl: {
    label: 'Blocked mid-crawl',
    style: 'bg-amber-100 text-amber-800',
    needsAttention: false,
    hint: 'The site started refusing us partway through, so the catalog was incomplete. Retried automatically.',
  },
  timeout: {
    label: 'Timed out',
    style: 'bg-amber-100 text-amber-800',
    needsAttention: false,
    hint: 'The site was too slow to answer. Retried automatically.',
  },
  cert_invalid: {
    label: 'Bad certificate',
    style: 'bg-amber-100 text-amber-800',
    needsAttention: false,
    hint: "The site's HTTPS certificate is broken. We fall back to plain http, so this usually still works.",
  },
  thin_content: {
    label: 'No content found',
    style: 'bg-gray-100 text-gray-800',
    needsAttention: true,
    hint: 'The site loaded but had almost nothing on it. Worth checking the website on file is the right one.',
  },
  dns_dead: {
    label: 'Domain is gone',
    style: 'bg-red-100 text-red-800',
    needsAttention: true,
    hint: "The domain no longer resolves. This will never fix itself — the listing's website needs correcting.",
  },
  host_unreachable: {
    label: 'Server not answering',
    style: 'bg-red-100 text-red-800',
    needsAttention: true,
    hint: "The domain resolves but the server refuses every connection — the site is down or has moved. Not a bot check, and it won't clear on a retry.",
  },
  redirect_loop: {
    label: 'Domain hijacked?',
    style: 'bg-red-100 text-red-800',
    needsAttention: true,
    hint: 'The domain redirects endlessly — usually a lapsed registration that someone else picked up. Check it before trusting anything crawled from it.',
  },
};

// Causes a person needs to act on. The rest resolve on their own.
export const ATTENTION_CAUSES = Object.keys(FAILURE_CAUSES)
  .filter((k) => FAILURE_CAUSES[k].needsAttention);

/** Pull the cause slug out of a run's error string, or null if it has none. */
export function causeFromError(error) {
  if (!error) return null;
  const m = String(error).match(/^([a-z_]+):/);
  return m && FAILURE_CAUSES[m[1]] ? m[1] : null;
}

export function causeMeta(cause) {
  return FAILURE_CAUSES[cause] || null;
}
